<script>
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';
import Chart from 'primevue/chart';
import utilsMixin from '@mixins/utilsMixin';
import moment from 'moment/moment';
import formatMixin from '@mixins/formatMixin';
import AkLabel from '@components/v2/general/AkLabel.vue';
import Dropdown from 'primevue/dropdown';
import AkSelect from '@components/v2/input/AkSelect.vue';
import {Vue3Lottie} from 'vue3-lottie';
import revenuemanagementService from '@services/revenuemanagementService';

export default {
  name: 'IndicatorCardGraphDialog',
  components: {AkSelect, AkLabel, Chart, AkDialogHeadless, Dropdown, Vue3Lottie},
  mixins: [utilsMixin, formatMixin],
  inject: {
    refresh: {default: null},
  },
  props: {
    indicator: {
      type: Object,
      required: false,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    interval: {
      type: String,
      required: true,
    },
    unit: {
      type: String,
      required: false,
    },
    information: {
      type: String,
      required: false,
    },
    legendLabels: {
      type: Object,
      required: true,
    },
    graphType: {
      type: String,
      required: true,
    },
    hideZeroLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showLabels: true,
      intervalOptions: [
        {label: 'Jour', value: 'daily'},
        {label: 'Semaine', value: 'weekly'},
        {label: 'Mois', value: 'monthly'},
        {label: 'Année', value: 'yearly'},
      ],
      intervalValue: null,
      indicatorValue: null,
      pendingRefresh: false,
    };
  },
  methods: {
    show() {
      this.$refs.fullScreen.show();
      this.intervalValue = this.interval;
      this.indicatorValue = this.indicator;
    },
    displayLabel(date) {
      if (this.intervalValue === 'monthly') {
        if (!date) return '-';
        return this.capitalize(moment(date).format('MMMM YYYY'));
      }
      if (this.intervalValue === 'yearly') {
        if (!date) return '-';
        return moment(date).format('YYYY');
      }
      return this.displayDate(date);
    },
    toggleShowLabels() {
      if (navigator.vibrate) navigator.vibrate(1);
      this.showLabels = !this.showLabels;
    },
    refreshIndicator() {
      if (!this.uuid || !this.key) return;
      this.pendingRefresh = true;
      revenuemanagementService
        .indicator(this.uuid, this.key, this.intervalValue)
        .then((data) => {
          this.indicatorValue = data.indicatorGraphDTO;
          this.pendingRefresh = false;
        })
        .catch(() => {
          this.pendingRefresh = true;
          this.refresh();
        });
    },
  },
  computed: {
    uuid() {
      return this.indicator.uuid;
    },
    key() {
      return this.indicator.key;
    },
    isLine() {
      return this.graphType === 'line';
    },
    mainMap() {
      const main = this.indicatorValue?.main ?? {};
      const keys = Object.keys(main).map((k) => this.displayLabel(k));
      const values = Object.values(main);
      return {
        keys,
        values,
      };
    },
    comparisonMap() {
      const main = Object.keys(this.indicatorValue?.main ?? {});
      const comp = Object.keys(this.indicatorValue?.comparison ?? {});
      while (comp.length < main.length) {
        comp.push(null);
      }
      const keys = [];
      const values = [];
      for (let i = 0; i < main.length; i++) {
        keys.push(this.displayLabel(comp[i]));
        if (comp[i]) values.push((this.indicatorValue?.comparison ?? {})[comp[i]]);
      }
      return {
        keys,
        values,
      };
    },
    chartData() {
      const data = {
        labels: this.mainMap.keys,
        datasets: [
          {
            label: this.legendLabels.main,
            data: this.mainMap.values,
            borderColor: 'rgb(0 45 109)',
            pointBackgroundColor: 'rgb(0 45 109)',
            pointBorderWidth: 0,
            backgroundColor: this.isLine ? null : 'rgb(0 45 109)',
            minBarLength: 0,
            fill: false,
            pointRadius: 4,
            tension: 0.4,
            xAxisID: 'x',
            spanGaps: true,
          },
          {
            label: this.legendLabels.comparison,
            data: this.comparisonMap.values,
            borderColor: '#3fb4e5',
            pointBackgroundColor: '#3fb4e5',
            backgroundColor: this.isLine ? null : '#3fb4e5',
            minBarLength: 0,
            borderDash: [10, 5],
            pointBorderWidth: 0,
            pointRadius: 4,
            fill: false,
            tension: 0.4,
            xAxisID: 'x1',
            spanGaps: true,
          },
        ],
      };
      return data;
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        onHover: (e, elements, chart) => {
          const nearest = chart.getElementsAtEventForMode(e, 'nearest', {intersect: true}, false);
          if (nearest && nearest.length) {
            chart.$hoveredDatasetIndex = nearest[0].datasetIndex;
          }
        },
        interaction: {
          mode: 'index',
          intersect: true,
        },
        spanGaps: true,
        plugins: {
          legend: {
            position: 'bottom',
            onClick: (e, legendItem) => {
              const chart = e.chart;
              const index = legendItem.datasetIndex;
              const meta = chart.getDatasetMeta(index);

              if (meta.hidden === null) {
                meta.hidden = !meta.hidden;
              } else {
                meta.hidden = null;
              }

              // Hide/show x1 axis labels based on comparison dataset visibility
              if (index === 1 && chart.options.scales.x1) {
                chart.options.scales.x1.display = meta.hidden !== true;
              }

              chart.update();
            },
            labels: {
              usePointStyle: true,
              boxHeight: 6,
              color: '#172344',
              font: {size: 13},
              filter: (item) => {
                const chart = item.chart;
                return item.datasetIndex < 2 && chart?.getDatasetMeta(item.datasetIndex)?.hidden !== true;
              },
            },
          },
          tooltip: {
            position: 'nearest',
            itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
            filter: (item) => item?.datasetIndex < 2,
            usePointStyle: true,
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems?.length) return '';
                const chart = tooltipItems[0].chart;
                const hoveredIndex = chart.$hoveredDatasetIndex ?? tooltipItems[0].datasetIndex;
                const item = tooltipItems.find((i) => i.datasetIndex === hoveredIndex) || tooltipItems[0];
                return item.label;
              },
              label: (context) => {
                let label =
                  this.unit === ' €' ? this.formatNumberRM2(context.parsed.y) : this.formatNumberRM(context.parsed.y);
                if (this.unit) label += this.unit;
                return label;
              },
              labelColor: (context) => {
                return {
                  borderColor: context.dataset.borderColor,
                  backgroundColor: context.dataset.borderColor,
                  borderWidth: 0,
                  borderRadius: 2,
                };
              },
            },
            backgroundColor: '#FFF',
            titleColor: '#293656',
            bodyColor: '#172344',
            displayColors: true,
            borderColor: '#e5e7eb',
            borderWidth: 1,
            boxHeight: 8,
            boxPadding: 4,
          },
        },
        scales: {
          x: {
            type: 'category',
            stacked: this.isLine,
            labels: this.mainMap.keys,
          },
          x1: {
            type: 'category',
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            labels: this.comparisonMap.keys,
          },
          y: {
            stacked: false,
            min: 0,
          },
        },
      };
    },
    chartPlugins() {
      // small helper to draw rounded rect
      function roundRect(ctx, x, y, w, h, r) {
        const radius = r || 4;
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + w - radius, y);
        ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
        ctx.lineTo(x + w, y + h - radius);
        ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
        ctx.lineTo(x + radius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      }

      return [
        {
          id: 'valueLabelsPlugin',
          afterDatasetsDraw: (chart) => {
            const ctx = chart.ctx;

            // Collect visible metas for the first two datasets only
            const visibleMetas = [0, 1].map((datasetIndex) => {
              const dataset = chart.data.datasets[datasetIndex];
              if (!dataset) return null;
              const meta = chart.getDatasetMeta(datasetIndex);
              if (!meta || !meta.data) return null;
              const datasetVisible =
                typeof chart.isDatasetVisible === 'function'
                  ? chart.isDatasetVisible(datasetIndex)
                  : !(meta.hidden || dataset.hidden === true);
              return datasetVisible ? {dataset, meta} : null;
            });

            // Draw labels for each dataset
            visibleMetas.forEach((entry, datasetIndex) => {
              if (!entry || !this.showLabels) return;
              const {dataset, meta} = entry;
              if (!chart.isDatasetVisible(datasetIndex)) return;
              const otherEntry = visibleMetas[datasetIndex === 0 ? 1 : 0];

              meta.data.forEach((element, index) => {
                const value = dataset.data[index];
                if (value === null || value === undefined || (value === 0 && this.hideZeroLabels)) return;

                const x = element.x;
                const y = element.y;

                // Determine if this point is the lower one at this x index.
                // In canvas coordinates, a higher y value means lower on screen.
                let placeBelow = false;
                if (otherEntry && this.isLine) {
                  const otherElement = otherEntry.meta.data[index];
                  const otherValue = otherEntry.dataset.data[index];
                  if (otherElement && otherValue !== null && otherValue !== undefined) {
                    placeBelow = y >= otherElement.y;
                  }
                }

                // prepare text
                let text =
                  typeof value === 'number'
                    ? this.unit === ' €'
                      ? this.formatNumberRM2(value)
                      : this.formatNumberRM(value)
                    : String(value);
                if (this.unit) text += this.unit;

                ctx.save();
                // small card style
                const fontSize = 10;
                ctx.font = `${fontSize}px Inter, ui-sans-serif, system-ui, -apple-system, 'Helvetica Neue', Arial`;

                const paddingH = 16; // horizontal padding
                const paddingV = 8; // vertical padding
                const textWidth = ctx.measureText(text).width;
                const rectWidth = textWidth + paddingH;
                const rectHeight = fontSize + paddingV;

                const gap = 10; // gap from the point
                let rectX = x - rectWidth / 2;
                let textX = x;

                // Prevent label from being cut off by the chart area edges
                const margin = 4;
                const leftEdge = (chart.chartArea?.left ?? 0) + margin;
                const rightEdge = (chart.chartArea?.right ?? chart.width) - margin;

                if (rectX < leftEdge) {
                  const shift = leftEdge - rectX;
                  rectX += shift;
                  textX += shift;
                } else if (rectX + rectWidth > rightEdge) {
                  const shift = rectX + rectWidth - rightEdge;
                  rectX -= shift;
                  textX -= shift;
                }

                // If placing below would push the label outside the chart area, force it above instead
                const chartBottom = chart.chartArea?.bottom ?? chart.height;
                if (placeBelow && y + gap + rectHeight > chartBottom) {
                  placeBelow = false;
                }

                // If placing above would push the label outside the chart area, force it below instead
                const chartTop = chart.chartArea?.top ?? 0;
                if (!placeBelow && y - gap - rectHeight < chartTop) {
                  placeBelow = true;
                }

                // choose position: prefer above, but if intersection with already placed rects or off-canvas, choose below
                let chosenRect = placeBelow
                  ? {x: rectX, y: y + gap, w: rectWidth, h: rectHeight}
                  : {x: rectX, y: y - rectHeight - gap, w: rectWidth, h: rectHeight};

                // draw shadow slightly larger than card, colored with the dataset's line color at 40% opacity
                ctx.globalAlpha = 0.4;
                ctx.fillStyle = dataset.borderColor ?? 'rgba(0,0,0,0.4)';
                roundRect(ctx, chosenRect.x - 1, chosenRect.y - 1, chosenRect.w + 2, chosenRect.h + 2, 7);
                ctx.fill();
                ctx.globalAlpha = 1;

                // white rounded card
                ctx.fillStyle = '#ffffff';
                roundRect(ctx, chosenRect.x, chosenRect.y, chosenRect.w, chosenRect.h, 6);
                ctx.fill();

                // text
                ctx.fillStyle = '#172344'; // gray-900
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, textX, chosenRect.y + chosenRect.h / 2);

                ctx.restore();
              });
            });
          },
        },
      ];
    },
  },
  watch: {
    showLabels() {
      this.$refs.chart.refresh();
    },
    interval(newVal, oldVal) {
      if (this.pendingRefresh && newVal) {
        this.refreshIndicator();
      }
    },
  },
};
</script>

<template>
  <AkDialogHeadless
    ref="fullScreen"
    dismissable
    width="90vw"
    :show-cancel-button="false"
    :show-validate-button="false"
    content-class="!px-2 md:!px-6"
    class="md:rounded-xl rounded-t-3xl overflow-hidden">
    <div class="flex md:flex-row flex-col justify-between gap-4 px-2 md:px-0">
      <AkLabel labelClass="!text-[18px] !font-medium text-grey-900" class="!mb-0" :information="information"
        >{{ label }}
      </AkLabel>
      <div class="flex gap-3 md:pr-4 grow md:grow-0 justify-between flex-row-reverse md:flex-row md:justify-normal">
        <button
          @click="toggleShowLabels"
          class="h-[39px] aspect-square flex items-center justify-center border rounded-[10px] border-grey-100 hover:bg-grey-50 hover:border-grey-200">
          <i class="ga-icon text-md text-grey-900" :class="showLabels ? 'ga-eye' : 'ga-eye-slash'" />
        </button>
        <AkSelect
          xs
          :options="intervalOptions"
          v-model="intervalValue"
          class="w-fit flex items-center"
          option-label="label"
          option-value="value"
          @update:modelValue="refreshIndicator">
        </AkSelect>
      </div>
    </div>
    <div class="flex gap-12 items-center justify-center mt-6">
      <Chart
        v-if="this.indicatorValue && !pendingRefresh"
        ref="chart"
        :key="chartData.labels.join('-')"
        :data="chartData"
        :type="graphType"
        :options="chartOptions"
        :plugins="chartPlugins"
        class="md:h-[76vh] h-[60vh] min-w-0 w-full" />
      <div v-else class="md:h-[76vh] h-[60vh]">
        <Vue3Lottie :animationData="require('@assets/animations/line_chart_loading.json')" :loop="true" />
      </div>
    </div>
  </AkDialogHeadless>
</template>

<style scoped></style>
